import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { ApiError } from '@/domains/api';
import { AuthShell, tokenFromLink } from '@/domains/auth';
import { publicMailApi } from '../api';

/** Where the address stands, as this page last changed it. */
type Stage = 'ready' | 'unsubscribed' | 'resubscribed';

type Status = 'checking' | 'idle' | 'busy' | 'failed' | 'invalid';

/**
 * `/unsubscribe` — where a bulk mail's footer links (SR-523 stage 3), token in the fragment.
 *
 * **It unsubscribes on a press, never on load** (the lead's ruling): mail gateways — Outlook's
 * Safe Links, Mimecast — open a mail's links in a sandbox that runs the page, and a page that
 * acted on load would unsubscribe whoever's mail was scanned. On load it only reads the state
 * (`GET`, which changes nothing), so an address already unsubscribed is offered Resubscribe. Mail
 * clients' own one-click unsubscribe is the `List-Unsubscribe-Post` header, a POST of their own.
 *
 * **It says nothing about the person.** survey-api answers the same for every valid token and
 * names no address; a bad, tampered or expired token is one fixed message. Anonymous, outside
 * `ProtectedRoute`: whoever reads the mail may have no account, and a session this browser holds
 * is neither sent nor used.
 */
export function UnsubscribePage() {
  const { hash, search } = useLocation();
  const token = tokenFromLink(hash, search).trim();
  const [stage, setStage] = React.useState<Stage>('ready');
  const [status, setStatus] = React.useState<Status>(token ? 'checking' : 'invalid');

  React.useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    publicMailApi.state(token, controller.signal).then(
      (answer) => {
        if (controller.signal.aborted) return;
        setStage(answer.unsubscribed ? 'unsubscribed' : 'ready');
        setStatus('idle');
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        // A read that failed proves nothing about the link: the button stays.
        setStatus(error instanceof ApiError && error.status === 400 ? 'invalid' : 'idle');
      }
    );
    return () => controller.abort();
  }, [token]);

  const act = async (action: 'unsubscribe' | 'resubscribe') => {
    setStatus('busy');
    try {
      if (action === 'unsubscribe') await publicMailApi.unsubscribe(token);
      else await publicMailApi.resubscribe(token);
      setStage(action === 'unsubscribe' ? 'unsubscribed' : 'resubscribed');
      setStatus('idle');
    } catch (error) {
      setStatus(error instanceof ApiError && error.status === 400 ? 'invalid' : 'failed');
    }
  };

  const heading = status === 'invalid' ? 'This link cannot be used' : HEADINGS[stage];
  const busy = status === 'busy' || status === 'checking';
  return (
    <AuthShell title={heading} zoneName={null}>
      <h1 className="text-2xl text-[var(--color-royal-blue)]" data-unsubscribe={status === 'invalid' ? 'invalid' : stage}>
        {heading}
      </h1>
      {status === 'invalid' ? (
        <p className="mt-2 text-sm text-muted-foreground">
          The link is not valid or has expired. Use the unsubscribe link of a more recent email from
          Veritant, or reply to one and ask.
        </p>
      ) : stage === 'resubscribed' ? (
        <p className="mt-2 text-sm text-muted-foreground">You will receive them again.</p>
      ) : (
        <>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            {stage === 'unsubscribed'
              ? 'Survey invitations, reminders and outreach from Veritant no longer reach this address. Emails about something you did yourself — a submission, a sign-in — still do.'
              : "Stop Veritant's survey invitations, reminders and outreach emails to the address this email was sent to. You can resubscribe here afterwards."}
          </p>
          {status === 'failed' && (
            // An answer that never arrived proves nothing; the button stays for another try.
            <p role="alert" className="mb-4 text-sm font-medium text-destructive">
              The request did not complete. Try again.
            </p>
          )}
          <Button
            type="button"
            variant={stage === 'unsubscribed' ? 'outline' : 'default'}
            className="w-full"
            disabled={busy}
            onClick={() => void act(stage === 'unsubscribed' ? 'resubscribe' : 'unsubscribe')}
          >
            {busy ? 'One moment…' : stage === 'unsubscribed' ? 'Resubscribe' : 'Unsubscribe'}
          </Button>
        </>
      )}
    </AuthShell>
  );
}

const HEADINGS: Record<Stage, string> = {
  ready: 'Unsubscribe from Veritant survey emails',
  unsubscribed: 'You are unsubscribed from Veritant survey emails',
  resubscribed: 'You are subscribed again'
};
